import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
    Grid,
    TextField, 
    MenuItem, 
    Typography,
    Tooltip
} from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';
import { StoreMultiAutocomplete } from '../../../../components/Common';

export interface StoreFormData {
    currency_id: string;
    identifier: string;
    name: string;
    entity_type: string;
    subscribe_date: string;
    unsubscribe_date: string;
    franchise: string;
    signature: string;
    entity_number: string;
    latitude: string;
    longitude: string;
    transaction_integration_mode: string;
    opening_hours: string;
    description: string;
    payment_overdue: string;
    add_depend_on: number[];
}

interface StoreInformationFormProps {
    data: StoreFormData;
    onChange: (field: keyof StoreFormData, value: string | number[]) => void;
    listStoresOptions: any[];
    storesLoading: boolean;
}

const StoreInformationForm = ({ data, onChange, listStoresOptions, storesLoading }: StoreInformationFormProps) => {
    const { t } = useLanguage();
    const [errors, setErrors] = useState<Record<string, string>>({});
    
    // Refs pour les inputs textuels non-contrôlés
    const nameRef = useRef<HTMLInputElement>(null);
    const identifierRef = useRef<HTMLInputElement>(null);
    const entityNumberRef = useRef<HTMLInputElement>(null);
    const latitudeRef = useRef<HTMLInputElement>(null);
    const longitudeRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLInputElement>(null);
    const signatureRef = useRef<HTMLInputElement>(null);
    const openingHoursRef = useRef<HTMLInputElement>(null);

    const validationRules: Record<string, any> = {
        'currency_id': { required: true, type: 'string', max_length: 10 },
        'identifier': { required: true, type: 'string', max_length: 10 },
        'name': { required: true, type: 'string', max_length: 50 },
        'entity_type': { required: true, type: 'string', max_length: 20 },
        'subscribe_date': { required: true, type: 'datetime' },
        'franchise': { required: true, type: 'string', max_length: 1 },
        'signature': { type: 'string', max_length: 100 },
        'entity_number': { type: 'string', max_length: 6 },
        'latitude': { type: 'string', max_length: 24 },
        'longitude': { type: 'string', max_length: 24 },
        'transaction_integration_mode': { type: 'string', max_length: 10 },
        'opening_hours': { type: 'string', max_length: 4000 },
        'description': { type: 'string', max_length: 4000 },
        'payment_overdue': { type: 'string', max_length: 12 }
    };

    const validateField = useCallback((fieldName: string, value: any) => {
        const rule = validationRules[fieldName];
        if (!rule) return '';

        if (rule.required && (!value || value.toString().trim() === '')) {
            return t('common.requiredField');
        }

        if (rule.max_length && value && value.toString().length > rule.max_length) {
            return t('common.maxLength', { max: rule.max_length });
        }

        return '';
    }, [t]);

    const handleFieldChange = useCallback((field: keyof StoreFormData, value: string) => {
        onChange(field, value);
        
        // Validation en temps réel
        const error = validateField(field, value);
        setErrors((prev) => ({ 
            ...prev, 
            [field]: error 
        }));
    }, [onChange, validateField]);
    
    const handleTextBlur = useCallback((field: keyof StoreFormData, ref: React.RefObject<HTMLInputElement | null>) => {
        const value = ref.current?.value || '';
        handleFieldChange(field, value);
    }, [handleFieldChange]);

    const canDependOn = useCallback((storeType: string, dependOnType: string) => {
        const normalizedStoreType = storeType;
        const normalizedDependType = dependOnType;
        
        if (normalizedStoreType === "STATISTIC" && (normalizedDependType === "STATISTIC" || normalizedDependType === "STORE")) {
            return false;
        }
        
        if (normalizedStoreType === "STORE" && normalizedDependType === "STORE") {
            return false;
        }
        
        return true;
    }, []);

    const filteredOptions = useMemo(() => {
        return (listStoresOptions || []).filter(option => {
            if (!option || !option.value) return false;
            
            let optionEntityType = null;
            
            if (option.data && option.data.ENTITY_TYPE) {
                optionEntityType = option.data.ENTITY_TYPE;
            } else if (option.entity_type) {
                optionEntityType = option.entity_type;
            }
            
            if (!optionEntityType) return true;
            
            return canDependOn(data.entity_type, optionEntityType);
        });
    }, [listStoresOptions, canDependOn, data.entity_type]);

    const dependencyOptions = useMemo(() => (
        filteredOptions.map((option) => ({
            ...option.data,
            ENTITY_ID: option.data?.ENTITY_ID || option.value,
        }))
    ), [filteredOptions]);

    const selectedDependencies = useMemo(() => {
        const selectedIds = new Set((data.add_depend_on || []).map((id) => id.toString()));
        return dependencyOptions.filter((option) => selectedIds.has(option.ENTITY_ID?.toString()));
    }, [data.add_depend_on, dependencyOptions]);

    // Format date for display (handle empty strings)
    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        // If it's already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
        // Otherwise try to parse and format
        try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch {
            return '';
        }
    };

    return (
        <Grid container spacing={2}>
            {/* Line 1: Nom + Mode intégration transaction + Règlement préalable + Franchise + Devise */}
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={nameRef}
                    required
                    size="small"
                    fullWidth
                    label={t('pages.store.name')}
                    defaultValue={data.name}
                    onBlur={() => handleTextBlur('name', nameRef)}
                    error={!!errors.name}
                    inputProps={{ maxLength: 50 }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    size="small"
                    fullWidth
                    label={t('pages.store.transactionMode')}
                    select
                    value={data.transaction_integration_mode}
                    onChange={(e) => handleFieldChange("transaction_integration_mode", e.target.value)}
                >
                    <MenuItem value="0">{t('common.no')}</MenuItem>
                    <MenuItem value="1">{t('common.yes')}</MenuItem>
                    <MenuItem value="WS">WEBSERVICE</MenuItem>
                </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    size="small"
                    select
                    fullWidth
                    label={t('pages.store.advancePayment')}
                    value={data.payment_overdue}
                    onChange={(e) => handleFieldChange("payment_overdue", e.target.value)}
                >
                    <MenuItem value="0">{t('common.no')}</MenuItem>
                    <MenuItem value="1">{t('common.yes')}</MenuItem>
                </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    size="small"
                    select
                    fullWidth
                    label={t('pages.store.franchise')}
                    value={data.franchise}
                    onChange={(e) => handleFieldChange("franchise", e.target.value)}
                >
                    <MenuItem value="0">{t('pages.store.integreLabel')}</MenuItem>
                    <MenuItem value="1">{t('pages.store.franchiseLabel')}</MenuItem>
                </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    size="small"
                    select
                    fullWidth
                    label={t('pages.store.currency')}
                    value={data.currency_id}
                    onChange={(e) => handleFieldChange("currency_id", e.target.value)}
                >
                    <MenuItem value="1">Euro</MenuItem>
                </TextField>
            </Grid>

            {/* Line 2: Identifiant* + Entity_number + Type de magasin */}
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={identifierRef}
                    required
                    size="small"
                    fullWidth
                    label={t('pages.store.storeNumber')}
                    defaultValue={data.identifier}
                    onBlur={() => handleTextBlur('identifier', identifierRef)}
                    error={!!errors.identifier}
                    inputProps={{ maxLength: 10 }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={entityNumberRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.internalCode')}
                    defaultValue={data.entity_number}
                    onBlur={() => handleTextBlur('entity_number', entityNumberRef)}
                    error={!!errors.entity_number}
                    inputProps={{ maxLength: 6 }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    size="small"
                    select
                    fullWidth
                    label={t('pages.store.type')}
                    value={data.entity_type}
                    onChange={(e) => handleFieldChange("entity_type", e.target.value)}
                >
                    <MenuItem value="STORE">STORE</MenuItem>
                    <MenuItem value="COMPANY">COMPANY</MenuItem>
                    <MenuItem value="STATISTIC">STATISTIC</MenuItem>
                </TextField>
            </Grid>

            {/* Line 3: Latitude + Longitude + Date d'abonnement + Date de désabonnement */}
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={latitudeRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.latitude')}
                    defaultValue={data.latitude}
                    onBlur={() => handleTextBlur('latitude', latitudeRef)}
                    error={!!errors.latitude}
                    inputProps={{ maxLength: 24 }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={longitudeRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.longitude')}
                    defaultValue={data.longitude}
                    onBlur={() => handleTextBlur('longitude', longitudeRef)}
                    error={!!errors.longitude}
                    inputProps={{ maxLength: 24 }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    fullWidth
                    required
                    label={t('pages.store.subscribeDate')}
                    type="date"
                    value={formatDate(data.subscribe_date)}
                    onChange={(e) => handleFieldChange("subscribe_date", e.target.value)}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    error={!!errors.subscribe_date}
                    size="small"
                />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    fullWidth
                    label={t('pages.store.unsubscribeDate')}
                    type="date"
                    value={formatDate(data.unsubscribe_date)}
                    onChange={(e) => handleFieldChange("unsubscribe_date", e.target.value)}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    size="small"
                />
            </Grid>

            {/* Line 4: Description + Signature + opening_hours + Dépend de */}
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={descriptionRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.description')}
                    multiline
                    rows={4}
                    defaultValue={data.description}
                    onBlur={() => handleTextBlur('description', descriptionRef)}
                    error={!!errors.description}
                    inputProps={{ maxLength: 4000 }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={signatureRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.signature')}
                    multiline
                    rows={4}
                    defaultValue={data.signature}
                    onBlur={() => handleTextBlur('signature', signatureRef)}
                    error={!!errors.signature}
                    inputProps={{ maxLength: 100 }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    inputRef={openingHoursRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.openingHours')}
                    multiline
                    rows={4}
                    defaultValue={data.opening_hours}
                    onBlur={() => handleTextBlur('opening_hours', openingHoursRef)}
                    error={!!errors.opening_hours}
                    inputProps={{ maxLength: 4000 }}
                />
            </Grid>
            
            {/* Dépend de section - only show if not COMPANY */}
            {data.entity_type !== "COMPANY" && (
                <Grid size={{ xs: 12, md: 4 }}>
                    <div style={{ border: "1px solid #ccc", borderRadius: "4px", height: "200px", padding: "12px", boxSizing: "border-box" }}>
                        <Tooltip 
                            title={
                                data.entity_type === "STATISTIC"
                                    ? t('pages.store.statisticOnlyCompany')
                                    : data.entity_type === "STORE"
                                        ? t('pages.store.storeCanDependOn')
                                        : ""
                            }
                            placement="top"
                        >
                            <div style={{ height: "100%" }}>
                                <StoreMultiAutocomplete
                                    stores={dependencyOptions}
                                    value={selectedDependencies}
                                    onChange={(selectedStores) => onChange('add_depend_on', selectedStores.map((store) => Number(store.ENTITY_ID)))}
                                    label={t('pages.store.dependsOn')}
                                    placeholder={t('common.search')}
                                    loading={storesLoading}
                                />
                            </div>
                        </Tooltip>
                    </div>
                </Grid>
            )}
        </Grid>
    );
};

export default StoreInformationForm;
